import React, { useState, useEffect, useMemo } from 'react';
import Page from '../UI/Page';
import { 
  Box, 
  Heading, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Flex, 
  Tooltip, 
  Spinner, 
  Center, 
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  Card,
  CardBody
} from "@chakra-ui/react";
import proxy from '../../proxy';
import axios from "axios";
import Stock from './Stock';

const Stocks = () => {
  const [stocks, setStocks] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await axios.get(`${proxy}server_mts/GetStocks.php`, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!response.data || !Array.isArray(response.data)) {
          throw new Error("Invalid stock data received.");
        }

        setStocks(response.data);
      } catch (error) {
        console.error("Error fetching stocks:", error.message);
        setError("Unable to retrieve the stock data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStocks();
  }, []);
  
  // State to track current prices from Stock components
  const [currentPrices, setCurrentPrices] = useState({});

  const { totalDividends, totalPaidDividends, totalValue, totalInvested, totalProfit, totalCommissions, avgDividendYield } = useMemo(() => {
    const totalDividends = stocks.reduce((sum, stock) => {
      const yearly = stock.div_share * stock.div_per_year * stock.nr_shares;
      return sum + yearly;
    }, 0);

    const totalPaidDividends = stocks.reduce((sum, stock) => {
      return sum + Number(stock.paid_div || 0);
    }, 0);

    const totalInvested = stocks.reduce((sum, stock) => {
      return sum + (stock.average_price_share * stock.nr_shares);
    }, 0);

    const totalCommissions = stocks.reduce((sum, stock) => {
      return sum + Number(stock.commission || 0);
    }, 0);

    // Calculate total current value using prices from Stock components
    const totalValue = stocks.reduce((sum, stock) => {
      const currentPrice = currentPrices[stock.id] || stock.average_price_share;
      return sum + (currentPrice * stock.nr_shares);
    }, 0);

    const totalProfit = totalValue - totalInvested;

    // Calculate average dividend yield
    const avgDividendYield = totalInvested > 0 ? ((totalDividends / totalInvested) * 100).toFixed(2) : 0;

    return { 
      totalDividends, 
      totalPaidDividends, 
      totalValue, 
      totalInvested, 
      totalProfit,
      totalCommissions,
      avgDividendYield
    };
  }, [stocks, currentPrices]);

  const totalProfitPercent = totalInvested > 0 ? ((totalProfit / totalInvested) * 100).toFixed(2) : 0;
  const recoveryPercent = totalInvested > 0 ? ((totalPaidDividends / (totalInvested + totalCommissions)) * 100).toFixed(2) : 0;

  const bestStock = stocks.reduce((best, stock) => {
  const profit = (stock.current_price - stock.average_price_share) / stock.average_price_share * 100;
  const bestProfit = (best.current_price - best.average_price_share) / best.average_price_share * 100;
  return profit > bestProfit ? stock : best;
}, stocks[0]);

  if (isLoading) {
    return (
      <Page>
        <Center minH="70vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.300" thickness="4px" speed="0.65s" />
            <Text color="gray.400" fontSize="sm">Loading Mattias portfolio...</Text>
          </VStack>
        </Center>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Center minH="70vh">
          <VStack spacing={4}>
            <Text fontSize="5xl">📉</Text>
            <Text color="red.400" fontSize="lg" fontWeight="semibold">
              {error}
            </Text>
            <Text color="gray.500" fontSize="sm">
              Please refresh the page or check your connection
            </Text>
          </VStack>
        </Center>
      </Page>
    );
  }

  return (
    <Page>
      <div className="hero" style={{ minHeight: '70vh' }}>
        <VStack spacing={6} p={{ base: 2, md: 4 }} mt={6}>
          
          {/* Header Section */}
          <VStack spacing={1} mb={4}>
            <Heading size="sm" color="teal.300" fontWeight="semibold">
              Mattias Assets Portfolio
            </Heading>
            <Text fontSize="xs" color="gray.500">📈 Investment Tracker</Text>
          </VStack>

          {/* Summary Statistics Cards */}
          <SimpleGrid 
            columns={{ base: 2, md: 4, lg: 4 }} 
            spacing={4} 
            width="100%"
            mb={4}
          >
            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Total Stocks
                  </StatLabel>
                  <StatNumber color="purple.300" fontSize={{ base: "lg", md: "xl" }}>
                    {stocks.length}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Companies
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg="gray.800" borderWidth="1px" borderColor="gray.700" boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s">
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Best Performer
                  </StatLabel>
                  <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                    {bestStock?.ticker || 'N/A'}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Top stock
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Income / Year
                  </StatLabel>
                  <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                    ${totalDividends.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Expected annually
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Paid Dividends
                  </StatLabel>
                  <StatNumber color="yellow.300" fontSize={{ base: "lg", md: "xl" }}>
                    ${totalPaidDividends.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Received so far
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

          </SimpleGrid>

          {/* Table Section */}
          <Flex justify="center" align="center" width="100%" p={1}>
            <Box
              p={4}
              borderWidth="1px"
              borderColor="gray.700"
              borderRadius="lg"
              boxShadow="lg"
              width="100%"
              overflowX="auto"
              bg="gray.800"
            >
              <Heading size="sm" mb={4} textAlign="center" color="teal.300">
                📊 Stocks & Dividends Details
              </Heading>

              {stocks.length === 0 ? (
                <Center py={10}>
                  <VStack spacing={3}>
                    <Text fontSize="4xl">📉</Text>
                    <Text color="gray.500" fontSize="md">
                      No stock data available
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      Start investing to build your portfolio
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <Table variant="simple" size="sm" colorScheme="teal">
                  <Thead bg="gray.900">
                    <Tr>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2} py={3}>
                        <Tooltip label="The name of the company you've invested in" hasArrow>
                          Company
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
                        <Tooltip label="The stock ticker symbol (ex: AAPL, MSFT)" hasArrow>
                          Ticker
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Shares</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Price</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Value</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Avg/Share</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Acquisition</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Profit</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
                        <Tooltip label="Percentage gain/loss compared to initial investment" hasArrow>
                          Profit %
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
                        <Tooltip label="Annual dividend per share" hasArrow>
                          Div/Share
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
                        <Tooltip label="Total dividend you expect to receive per year" hasArrow>
                          Income/Yr
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
                        <Tooltip label="Dividends received so far from this stock" hasArrow>
                          Paid Div
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
                        <Tooltip label="Percentage of investment recovered through dividends" hasArrow>
                          % Recover
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Commission</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stocks.map((st) => (
                      <Stock 
                        stock={st} 
                        key={st.id}
                        onPriceUpdate={(price) => {
                          setCurrentPrices(prev => ({
                            ...prev,
                            [st.id]: price
                          }));
                        }}
                      />
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </Flex>
        </VStack>
      </div>
    </Page>
  );
};

export default Stocks;